import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import BarraFiltri from "@/components/BarraFiltri";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, User, Clock, ExternalLink, AlertTriangle, CheckCircle2, Camera } from "lucide-react";
import { fileUrl } from "@/lib/placeholders";

const FIELD_LABELS = {
  segnaletica_ok: "Segnaletica correttamente posizionata",
  cantiere_delimitato: "Cantiere delimitato e accesso controllato",
  cassetta_ps_ok: "Cassetta primo soccorso presente e revisionata",
  estintori_ok: "Estintori presenti e revisionati",
  materiale_assorbente_ok: "Materiale assorbente presente",
  esplosimetro_ok: "Esplosimetro presente e revisionato",
  figure_sicurezza_presenti: "Presenti le 3 figure di sicurezza",
  dpi_corretti: "DPI indossati correttamente",
  macchine_idonee: "Macchine e attrezzature idonee",
  strumenti_tarati: "Strumenti di misura tarati",
  documentazione_tecnica_ok: "Documentazione tecnica POS + I.O. presente",
  schede_sicurezza_ok: "Schede di sicurezza prodotti presenti",
  presenza_scavo: "Presenza di scavo",
  gruppo_elettrogeno_ok: "Gruppo elettrogeno conforme",
  terzisti_gestiti: "Terzisti/subappaltatori gestiti",
  simulazione_effettuata: "Simulazione di emergenza effettuata",
};
const ANSWER_LABEL = { si: "Sì", no: "No", nd: "N.D." };
const ANSWER_COLOR = { si: "bg-emerald-100 text-emerald-700", no: "bg-red-100 text-red-700", nd: "bg-slate-100 text-slate-600" };

export default function VerificheCantiere() {
  const [list, setList] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("all");
  // Lo storico cresce a ogni verifica: senza una ricerca, dopo un anno
  // trovare quella di un cantiere preciso vuol dire scorrere tutto.
  const [cerca, setCerca] = useState("");
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);

  const load = async (site) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (site && site !== "all") params.append("site_id", site);
      const { data } = await api.get(`/site-checks?${params.toString()}`);
      setList(data);
    } catch { toast.error("Errore caricamento verifiche"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/sites").then(({ data }) => setSites(data)).catch(() => {});
    load("all");
  }, []);

  const countNo = (answers) => Object.values(answers || {}).filter(v => v === "no").length;

  const q = cerca.trim().toLowerCase();
  const visibili = q
    ? list.filter((v) => JSON.stringify(v).toLowerCase().includes(q))
    : list;

  return (
    <div className="space-y-6" data-testid="verifiche-cantiere-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Cantieri</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Verifiche in cantiere</h1>
        <p className="text-sm text-slate-500 mt-1">Storico dei controlli effettuati dai tecnici</p>
      </div>

      <BarraFiltri
        filtri={[{
          valore: siteFilter === "all" ? "" : siteFilter,
          onChange: (v) => { const s = v || "all"; setSiteFilter(s); load(s); },
          tutti: "Tutti i cantieri",
          opzioni: sites.map((s) => ({ valore: s.id, nome: s.name })),
          testid: "filtro-cantiere",
        }]}
        cerca={cerca} onCerca={setCerca}
        segnaposto="Cantiere, tecnico o esito..."
        conteggio={visibili.length} etichetta="verifiche" />

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!loading && visibili.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          Nessuna verifica registrata{siteFilter !== "all" ? " per questo cantiere" : ""}.
        </CardContent></Card>
      )}

      <div className="space-y-1.5">
        {visibili.map(v => {
          const noCount = countNo(v.answers);
          return (
            <Card key={v.id} className="border-slate-200 hover:shadow-sm transition cursor-pointer" onClick={() => setOpenItem(v)}>
              <CardContent className="px-3 py-2 flex items-start gap-3">
                <div className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0 ${noCount > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
                  {noCount > 0 ? <AlertTriangle className="w-5 h-5 text-red-500" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{v.site_name}</span>
                    {noCount > 0
                      ? <Badge variant="destructive" className="text-[10px]">{noCount} criticità</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Tutto conforme</Badge>}
                    {v.photo_urls?.length > 0 && (
                      <Badge variant="outline" className="text-[10px]"><Camera className="w-3 h-3 mr-1" />{v.photo_urls.length}</Badge>
                    )}
                  </div>
                  {v.attivita_svolta && <p className="text-sm text-slate-600 truncate mt-1">{v.attivita_svolta}</p>}
                  <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-1 flex-wrap">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{v.tecnico_name || v.tecnico_email}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(v.created_at).toLocaleString("it-IT")}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {openItem && <CheckDetail check={openItem} onClose={() => setOpenItem(null)} />}
    </div>
  );
}

function CheckDetail({ check, onClose }) {
  const [full, setFull] = useState(null);

  useEffect(() => {
    api.get(`/site-checks/${check.id}`).then(({ data }) => setFull(data)).catch(() => toast.error("Errore caricamento dettaglio"));
  }, [check.id]);

  const answers = full?.answers || {};
  const filledFields = Object.keys(FIELD_LABELS).filter(k => answers[k]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Verifica · {check.site_name}
            {check.site_id && (
              <Link to={`/cantieri/${check.site_id}`} target="_blank" className="text-orange-600 inline-flex items-center gap-1">
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            )}
          </DialogTitle>
        </DialogHeader>

        {!full && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

        {full && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 border border-slate-200 rounded p-3">
              <div><span className="text-slate-500">Tecnico:</span> {full.tecnico_name}</div>
              <div><span className="text-slate-500">Data:</span> {new Date(full.created_at).toLocaleString("it-IT")}</div>
              {full.committente && <div><span className="text-slate-500">Committente:</span> {full.committente}</div>}
              {full.numero_commessa && <div><span className="text-slate-500">Commessa:</span> {full.numero_commessa}</div>}
              {full.fase_lavorativa && <div><span className="text-slate-500">Fase:</span> {full.fase_lavorativa}</div>}
              {full.presenti && <div className="col-span-2"><span className="text-slate-500">Presenti:</span> {full.presenti}</div>}
            </div>

            <div>
              <h3 className="font-display font-bold mb-2">Esito controlli</h3>
              <div className="space-y-1.5">
                {filledFields.map(k => (
                  <div key={k} className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="flex-1">{FIELD_LABELS[k]}</span>
                    <Badge className={`${ANSWER_COLOR[answers[k]]} border-0 text-[10px]`}>{ANSWER_LABEL[answers[k]]}</Badge>
                  </div>
                ))}
                {filledFields.length === 0 && <p className="text-slate-500 text-xs">Nessuna risposta registrata</p>}
              </div>
            </div>

            {full.dpi_note && (
              <div>
                <h3 className="font-display font-bold mb-1 text-xs uppercase text-slate-500">Note DPI</h3>
                <p className="text-slate-700">{full.dpi_note}</p>
              </div>
            )}
            {full.esito_note && (
              <div>
                <h3 className="font-display font-bold mb-1 text-xs uppercase text-slate-500">Note conclusive</h3>
                <p className="text-slate-700">{full.esito_note}</p>
              </div>
            )}

            {full.scenario && (
              <div>
                <h3 className="font-display font-bold mb-2">Simulazione di emergenza</h3>
                <div className="p-3 border border-orange-200 bg-orange-50 rounded space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{full.scenario.category}</Badge>
                    <span className="font-semibold text-sm">{full.scenario.title}</span>
                    <Badge className="bg-slate-800 text-white border-0 text-[10px] ml-auto">
                      {full.scenario.score.points}/{full.scenario.score.max_points} punti
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600">{full.scenario.description}</p>
                  <div className="space-y-1">
                    {full.scenario.questions.map(q => {
                      const ans = full.scenario.answers[q.id];
                      const color = ans === "corretta" ? "bg-emerald-100 text-emerald-700" : ans === "parziale" ? "bg-amber-100 text-amber-700" : ans === "errata" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500";
                      return (
                        <div key={q.id} className="flex items-center justify-between text-xs py-1">
                          <span className="flex-1">{q.text}</span>
                          <Badge className={`${color} border-0 text-[10px]`}>{ans || "non risposto"}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {full.photo_urls?.length > 0 && (
              <div>
                <h3 className="font-display font-bold mb-2">Foto</h3>
                <div className="flex gap-2 flex-wrap">
                  {full.photo_urls.map((u, i) => (
                    <a key={i} href={fileUrl(u)} target="_blank" rel="noopener noreferrer">
                      <img src={fileUrl(u)} alt="" className="w-20 h-20 object-cover rounded border border-slate-200" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {full.signature_base64 && (
              <div>
                <h3 className="font-display font-bold mb-2">Firma tecnico</h3>
                <img src={full.signature_base64} alt="Firma" className="border border-slate-200 rounded bg-white max-w-full h-24" />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
